import axios from 'axios';

const API = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api'
});

API.interceptors.request.use((config) => {
    const token = localStorage.getItem('hims_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

API.interceptors.response.use(
    (res) => res,
    (err) => {
        if (err.response?.status === 401) {
            localStorage.removeItem('hims_token');
            window.location.href = '/login';
        }

        if (err.response?.status === 500) {
            const serverMsg = err.response?.data?.error || err.response?.data?.message || '';
            const isEmptyField =
                serverMsg.includes('invalid input syntax') ||
                serverMsg.includes('null value in column') ||
                serverMsg.includes('violates not-null') ||
                serverMsg.includes('invalid input value');

            if (isEmptyField) {
                const friendly = new Error('Please fill in all required fields before submitting.');
                friendly.isFieldError = true;
                return Promise.reject(friendly);
            }
        }

        return Promise.reject(err);
    }
);

export const statsAPI = {
    get: () => API.get('/stats'),
};

export const opdAPI = {
    createVisit: (data) => API.post('/opd', data),
    getTodayVisits: (date) => API.get(`/opd/today${date ? `?date=${date}` : ''}`),
    getVisit: (id) => API.get(`/opd/${id}`),
    getPatientVisits: (patient_id) => API.get(`/opd/patient/${patient_id}`),
    updateVisit: (id, data) => API.put(`/opd/${id}`, data),
    getPatientTimeline: (patient_id, params) => API.get(`/opd/patient/${patient_id}/timeline`, { params }),
    getQueueDisplay: () => API.get('/opd/queue-display'),
};

export const appointmentsAPI = {
    getAll: (params) => API.get('/appointments', { params }),
    create: (data) => API.post('/appointments', data),
    update: (id, data) => API.put(`/appointments/${id}`, data),
    cancel: (id, data) => API.delete(`/appointments/${id}`, { data }),
    convertToVisit: (id) => API.post(`/appointments/${id}/convert`),
    getFees: () => API.get('/appointments/fees'),
    saveFee: (data) => API.post('/appointments/fees', data),
    getPanelCompanies: () => API.get('/appointments/panel-companies'),
    savePanelCompany: (data) => API.post('/appointments/panel-companies', data),
};

export const opdBillingAPI = {
    search: (q) => API.get('/opd-billing/search', { params: { q } }),
    calculate: (visit_id) => API.get(`/opd-billing/calculate/${visit_id}`),
    save: (data) => API.post('/opd-billing/save', data),
    getReport: (params) => API.get('/opd-billing/report', { params }),
};

export const timelineAPI = {
    getPatientTimeline: (patient_id, params) => API.get(`/opd/patient/${patient_id}/timeline`, { params }),
};

export const authAPI = {
    login: (data) => API.post('/auth/login', data),
    getMe: () => API.get('/auth/me'),
};

export const patientAPI = {
    register: (data) => API.post('/patients', data),
    search: (q, page = 1) => API.get(`/patients?q=${q}&page=${page}`),
    getById: (id) => API.get(`/patients/${id}`),
    getHistory: (id) => API.get(`/patients/${id}/history`),
    update: (id, data) => API.put(`/patients/${id}`, data),
};

export const aeAPI = {
    createVisit: (data) => API.post('/ae', data),
    getDashboard: () => API.get('/ae/dashboard'),
    assignBed: (data) => API.post('/ae/assign-bed', data),
    setDisposition: (data) => API.post('/ae/disposition', data),
    activateMCI: (data) => API.post('/ae/mci/activate', data),
    getMLCRegister: (date) => API.get('/ae/mlc-register', { params: date ? { date } : {} }),
    getAEReport: (date) => API.get('/ae/report', { params: date ? { date } : {} }),
};

export const labAPI = {
    getDashboard: (params) => API.get('/lab/dashboard', { params }),
    getTests: (category) => API.get(`/lab/tests${category ? `?category=${category}` : ''}`),
    getProfiles: () => API.get('/lab/profiles'),
    createOrder: (data) => API.post('/lab/orders', data),
    getWorklist: (params) => API.get('/lab/worklist', { params }),
    getOrderItems: (id) => API.get(`/lab/orders/${id}`),
    getPatientHistory: (patient_id) => API.get(`/lab/patient/${patient_id}/history`),
    enterResult: (data) => API.post('/lab/result', data),
    verifyResult: (data) => API.post('/lab/verify', data),
    getSampleQueue: () => API.get('/lab/sample-queue'),
    collectSample: (orderId) => API.post(`/lab/sample-collect/${orderId}`),
    scanSample: (sample_id) => API.post('/lab/sample-scan', { sample_id }),
    rejectSample: (data) => API.post('/lab/sample-reject', data),
    getQCLog: (date) => API.get('/lab/qc', { params: date ? { date } : {} }),
    addQCEntry: (data) => API.post('/lab/qc', data),
    getSampleTracking: (params) => API.get('/lab/sample-tracking', { params }),
    markSampleReceived: (order_id) => API.post(`/lab/sample-tracking/receive/${order_id}`),
    getTestParameters: (test_id) => API.get(`/lab/parameters/${test_id}`),
    getAllParameters: () => API.get('/lab/parameters/all'),
    addParameter: (data) => API.post('/lab/parameters', data),
    updateParameter: (id, data) => API.put(`/lab/parameters/${id}`, data),
};

export const ipdAPI = {
    getWards: () => API.get('/ipd/wards'),
    getWardBeds: (ward_id) => API.get(`/ipd/wards/${ward_id}/beds`),
    createWard: (data) => API.post('/ipd/wards', data),
    addBed: (data) => API.post('/ipd/beds', data),
    getAllBeds: (ward_id) => API.get('/ipd/beds', { params: ward_id ? { ward_id } : {} }),
    transferPatient: (data) => API.post('/ipd/transfer', data),
    getCensus: (ward_id) => API.get('/ipd/census', { params: ward_id ? { ward_id } : {} }),
    admitPatient: (data) => API.post('/ipd/admit', data),
    getAdmission: (id) => API.get(`/ipd/${id}`),
    addNote: (data) => API.post('/ipd/note', data),
    addVitals: (data) => API.post('/ipd/vitals', data),
    addMedication: (data) => API.post('/ipd/medication', data),
    getMedications: (admission_id) => API.get(`/ipd/${admission_id}`).then(r => r.data?.medications || []),
    stopMedication: (med_id) => API.delete(`/ipd/medication/${med_id}`),
    dischargePatient: (id, data) => API.put(`/ipd/${id}/discharge`, data),
    updateAdmission: (id, data) => API.patch(`/ipd/${id}`, data),
    getDischargedPatients: (params) => API.get('/ipd/discharged', { params }),
    deleteWard: (id) => API.delete(`/ipd/wards/${id}`),
    updateWardRate: (id, rate_per_day) => API.patch(`/ipd/wards/${id}/rate`, { rate_per_day }),
    getBillingData: (id) => API.get(`/ipd/${id}/billing`),
    saveBill: (data) => API.post('/ipd/billing/save', data),
};

export const pacsAPI = {
    getTests: (modality) => API.get(`/pacs/tests${modality ? `?modality=${modality}` : ''}`),
    createOrder: (data) => API.post('/pacs/orders', data),
    getWorklist: (params) => API.get('/pacs/worklist', { params }),
    getOrderDetail: (id) => API.get(`/pacs/orders/${id}`),
    submitReport: (data) => API.post('/pacs/report', data),
    getApprovalQueue: () => API.get('/pacs/approval-queue'),
    approveReport: (data) => API.post('/pacs/report/approve', data),
    rejectReport: (data) => API.post('/pacs/report/reject', data),
    linkDicom: (data) => API.post('/pacs/dicom/link', data),
    getDashboard: () => API.get('/pacs/dashboard'),
    getPatientHistory: (patient_id) => API.get(`/pacs/patient/${patient_id}/history`),
    getOrderBilling: (id) => API.get(`/pacs/orders/${id}/billing`),
    saveOrderBilling: (data) => API.post('/pacs/billing/save', data),
    getBillingReport: (params) => API.get('/pacs/billing/report', { params }),
    generateAIReport: (formData) => API.post('/rad/ai-report', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    generateAIReportFromOrthanc: (data) => API.post('/rad/ai-report/orthanc', data),
    markImagesReady: (data) => API.post('/pacs/orders/mark-images-ready', data),
    scheduleOrder: (data) => API.post('/pacs/orders/schedule', data),
    flagCriticalResult: (data) => API.post('/pacs/critical-alert', data),
    getCriticalAlerts: () => API.get('/pacs/critical-alerts'),
    acknowledgeCriticalAlert: (data) => API.post('/pacs/critical-alert/acknowledge', data),
    submitAmendment: (data) => API.post('/pacs/orders/amend', data),
    getAmendments: (id) => API.get(`/pacs/orders/${id}/amendments`),
    searchOrthanc: (data) => API.post('/pacs/orthanc/search', data),
};

export const psychiatryAPI = {
    getStats: () => API.get('/psychiatry/stats'),
    getCases: (params) => API.get('/psychiatry/cases', { params }),
    createCase: (data) => API.post('/psychiatry/cases', data),
    getCase: (id) => API.get(`/psychiatry/cases/${id}`),
    updateCase: (id, data) => API.put(`/psychiatry/cases/${id}`, data),
    addSession: (data) => API.post('/psychiatry/sessions', data),
    savePsychlops: (data) => API.post('/psychiatry/psychlops', data),
    getSupervision: () => API.get('/psychiatry/supervision'),
    addSupervision: (data) => API.post('/psychiatry/supervision', data),
};

export const reportsAPI = {
    daily: (date) => API.get('/reports/daily', { params: { date } }),
    range: (from, to) => API.get('/reports/range', { params: { from, to } }),
    ipdPatients: (from, to, status) => API.get('/reports/ipd-patients', { params: { from, to, status } }),
};

export const aeClinicalAPI = {
    addMAR: (data) => API.post('/ae-clinical/mar', data),
    getMAR: (visit_id) => API.get(`/ae-clinical/mar/${visit_id}`),
    deleteMAR: (id) => API.delete(`/ae-clinical/mar/${id}`),
    addFluid: (data) => API.post('/ae-clinical/fluids', data),
    getFluids: (visit_id) => API.get(`/ae-clinical/fluids/${visit_id}`),
    stopFluid: (id) => API.patch(`/ae-clinical/fluids/${id}/stop`),
    deleteFluid: (id) => API.delete(`/ae-clinical/fluids/${id}`),
    saveMLCDiagram: (data) => API.post('/ae-clinical/mlc-diagram', data),
    getMLCDiagram: (visit_id) => API.get(`/ae-clinical/mlc-diagram/${visit_id}`),
    getSummons: (visit_id) => API.get(`/ae-clinical/summons/${visit_id}`),
    addSummons: (data) => API.post('/ae-clinical/summons', data),
    deleteSummons: (id) => API.delete(`/ae-clinical/summons/${id}`),
};

export const icuAPI = {
    admitToICU: (data) => API.post('/icu/admit', data),
    getCensus: () => API.get('/icu/census'),
    getAdmission: (id) => API.get(`/icu/${id}`),
    updateAdmission: (id, data) => API.patch(`/icu/${id}`, data),
    discharge: (id, data) => API.post(`/icu/${id}/discharge`, data),
    recordVitals: (data) => API.post('/icu/vitals', data),
    addNote: (data) => API.post('/icu/notes', data),
    deleteNote: (id) => API.delete(`/icu/notes/${id}`),
};

export const physioAPI = {
    createReferral: (data) => API.post('/physio/referrals', data),
    getWorklist: () => API.get('/physio/worklist'),
    getReferral: (id) => API.get(`/physio/referrals/${id}`),
    updateReferral: (id, data) => API.patch(`/physio/referrals/${id}`, data),
    getPatientReferrals: (patient_id) => API.get(`/physio/patient/${patient_id}`),
    addSession: (data) => API.post('/physio/sessions', data),
    deleteSession: (id) => API.delete(`/physio/sessions/${id}`),
};

export const pharmacyWardAPI = {
    getPendingCount: () => API.get('/pharmacy/ward-requests/pending-count'),
    getWardRequests: (status) => API.get('/pharmacy/ward-requests', { params: status ? { status } : {} }),
    issueRequest: (id, data) => API.post(`/pharmacy/ward-requests/${id}/issue`, data),
    rejectRequest: (id, data) => API.post(`/pharmacy/ward-requests/${id}/reject`, data),
    getConsumables: (status) => API.get('/pharmacy/consumable-requests', { params: status ? { status } : {} }),
    createConsumable: (data) => API.post('/pharmacy/consumable-requests', data),
    issueConsumable: (id, data) => API.post(`/pharmacy/consumable-requests/${id}/issue`, data),
    rejectConsumable: (id) => API.post(`/pharmacy/consumable-requests/${id}/reject`),
    getReturns: (status) => API.get('/pharmacy/ward-returns', { params: status ? { status } : {} }),
    createWardReturn: (data) => API.post('/ipd/ward-returns', data),
    acceptReturn: (id) => API.post(`/pharmacy/ward-returns/${id}/accept`),
    getIssuanceReport: (params) => API.get('/pharmacy/reports/issuance', { params }),
    getReturnReport: (params) => API.get('/pharmacy/reports/returns', { params }),
    getUnifiedReport: (params) => API.get('/pharmacy/reports/unified', { params }),
    getAdmissionRequests: (admission_id) => API.get(`/ipd/ward-requests/${admission_id}`),
    createReturn: (data) => API.post('/ipd/ward-returns', data),
};

export const wardInventoryAPI = {
    getWards: () => API.get('/ward-inventory/wards'),
    createRequest: (data) => API.post('/ward-inventory', data),
    getMyRequests: (params) => API.get('/ward-inventory/my-requests', { params }),
    getPharmacyRequests: (status) => API.get('/ward-inventory/pharmacy', { params: status ? { status } : {} }),
    issueRequest: (id, data) => API.post(`/ward-inventory/${id}/issue`, data),
    rejectRequest: (id, data) => API.post(`/ward-inventory/${id}/reject`, data),
};

export const ipdBillingAPI = {
    getDoctors: () => API.get('/ipd-billing/doctors'),
    getConsultants: () => API.get('/ipd-billing/consultants'),
    saveConsultant: (data) => API.post('/ipd-billing/consultants', data),
    deleteConsultant: (id) => API.delete(`/ipd-billing/consultants/${id}`),
    calculateBill: (admission_id) => API.get(`/ipd-billing/calculate/${admission_id}`),
    saveBill: (data) => API.post('/ipd-billing/save', data),
    listBills: () => API.get('/ipd-billing/list'),
};

export const pharmacyAPI = {
    getDrugs: (params) => API.get('/pharmacy/drugs', { params }),
    addDrug: (data) => API.post('/pharmacy/drugs', data),
    updateDrug: (id, data) => API.put(`/pharmacy/drugs/${id}`, data),
    addStock: (data) => API.post('/pharmacy/stock', data),
    getDrugStock: (drug_id) => API.get(`/pharmacy/stock/${drug_id}`),
    getLowStock: () => API.get('/pharmacy/low-stock'),
    getInventory: (params) => API.get('/pharmacy/inventory', { params }),
    dispense: (data) => API.post('/pharmacy/dispense', data),
    getHistory: (params) => API.get('/pharmacy/history', { params }),
    getDispensingDetail: (id) => API.get(`/pharmacy/dispensing/${id}`),
};

export const bloodBankAPI = {
    getDashboard: () => API.get('/blood-bank/dashboard'),
    getDonors: (params) => API.get('/blood-bank/donors', { params }),
    addDonor: (data) => API.post('/blood-bank/donors', data),
    recordDonation: (data) => API.post('/blood-bank/donate', data),
    getInventory: (params) => API.get('/blood-bank/inventory', { params }),
    createRequest: (data) => API.post('/blood-bank/requests', data),
    getRequests: (params) => API.get('/blood-bank/requests', { params }),
    issueBlood: (data) => API.post('/blood-bank/issue', data),
    getHistory: (params) => API.get('/blood-bank/history', { params }),
};

export const queueAPI = {
    issueToken: (data) => API.post('/queue/token', data),
    getQueue: (dept) => API.get(`/queue/${dept}`),
    getAllQueues: () => API.get('/queue/all'),
    callNext: (department) => API.post('/queue/call-next', { department }),
    resetQueue: (department) => API.post('/queue/reset', { department }),
};

export const labInventoryAPI = {
    getDashboard: () => API.get('/lab-inventory/dashboard'),
    getItems: (params) => API.get('/lab-inventory/items', { params }),
    addItem: (data) => API.post('/lab-inventory/items', data),
    updateItem: (id, data) => API.put(`/lab-inventory/items/${id}`, data),
    addStock: (id, data) => API.post(`/lab-inventory/items/${id}/add-stock`, data),
    useStock: (id, data) => API.post(`/lab-inventory/items/${id}/use-stock`, data),
    adjustStock: (id, data) => API.post(`/lab-inventory/items/${id}/adjust`, data),
    getTransactions: (params) => API.get('/lab-inventory/transactions', { params }),
    getSuppliers: () => API.get('/lab-inventory/suppliers'),
    addSupplier: (data) => API.post('/lab-inventory/suppliers', data),
    getAlerts: () => API.get('/lab-inventory/alerts'),
};

export const serviceAPI = {
    getAll:   ()              => API.get('/services'),
    create:   (data)          => API.post('/services', data),
    saveIPD:  (data)          => API.post('/services/ipd', data),
    getIPD:   (admission_id)  => API.get(`/services/ipd/${admission_id}`),
    deleteIPD:(id)            => API.delete(`/services/ipd/${id}`),
};

export const procedureAPI = {
    getAll:       ()           => API.get('/procedures'),
    getItems:     (id)         => API.get(`/procedures/${id}/items`),
    create:       (data)       => API.post('/procedures', data),
    addItem:      (id, data)   => API.post(`/procedures/${id}/items`, data),
    saveIPD:      (data)       => API.post('/procedures/ipd', data),
    getIPD:       (admission_id) => API.get(`/procedures/ipd/${admission_id}`),
    updateStatus: (id, data)   => API.put(`/procedures/ipd/${id}/status`, data),
};

export default API;
